import * as React from 'react'
import { Star } from 'lucide-react'
import { useOwnReviews } from '@/hooks/useVendorReviews'
import { useOwnVendor } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { serverPage } from '@/lib/pagination'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { Pagination } from '@/components/ui/pagination'
import { ReviewItem, ReviewSummary } from '@/components/reviews/ShownReviews'
import { ReviewAnswers } from '@/components/reviews/ReviewAnswers'

const PUBLISHED = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * A vendor company's published reviews (docs/vendor-growth.spec.md, "Reviews"),
 * newest first, for everyone at the company: what its profile and its page on
 * csvenueview.com show, every one of them. A review waiting for approval isn't
 * here; the company's owners get an email when one is published. Owners reply
 * once to each and may report one; everyone sees where those stand.
 */
export function VendorReviewsPage() {
  const [page, setPage] = React.useState(1)
  const reviews = useOwnReviews(page)
  const company = useOwnVendor()
  const total = reviews.data?.total
  const lastPage = total === undefined ? null : serverPage(page, total, reviews.data?.reviews.length ?? 0).pageCount

  // Reviews can go (an event canceled, a review taken down), leaving this page
  // past the end: go back to the last page there is, during render rather than
  // after it, so the empty page is never drawn.
  if (lastPage !== null && lastPage > 0 && page > lastPage) setPage(lastPage)

  function body() {
    if (reviews.isPending) return <LoadingState label="Loading reviews…" />
    if (reviews.isError) return <ErrorState message={requestErrorMessage(reviews.error)} onRetry={() => void reviews.refetch()} />
    const list = reviews.data
    if (list.total === 0 || list.summary === null) {
      return (
        <EmptyState
          icon={Star}
          title="No reviews yet"
          description={`Couples and planners can review ${company.data?.name ?? 'your company'} from their event page once the event is over. Each review shows here once FLOH Venues has approved it.`}
        />
      )
    }
    const shown = serverPage(page, list.total, list.reviews.length)
    const companyName = company.data?.name ?? 'your company'
    return (
      <div className="space-y-4">
        <ReviewSummary summary={list.summary} />
        {list.canAnswer ? null : <p className="m-0 text-sm text-ink-muted">Only an owner can reply to or report a review.</p>}
        <div className="overflow-hidden rounded-md border border-line">
          <ul className="m-0 list-none divide-y divide-line p-0">
            {list.reviews.map((review) => (
              <ReviewItem key={review.id} review={review} className="px-4 py-3">
                <p className="m-0 text-xs text-ink-muted">Published {PUBLISHED.format(new Date(review.publishedAt))}</p>
                <ReviewAnswers review={review} companyName={companyName} canAnswer={list.canAnswer} />
              </ReviewItem>
            ))}
          </ul>
          <Pagination page={shown.page} pageCount={shown.pageCount} from={shown.from} to={shown.to} total={shown.total} unit="reviews" onPage={setPage} />
        </div>
      </div>
    )
  }

  return (
    <PageContainer className="space-y-6">
      <PageHeader title="Reviews" description="What couples and planners said, as your profile and your page show it." />
      {body()}
    </PageContainer>
  )
}
